// One uncategorized transaction row, rendered per-band per `#s06`:
// phone/tablet render a card with suggestion chips; desktop renders a dense
// grid row with Accept/Other text links plus a bulk-select checkbox.
// Correcting the category is what raises the apply-to-similar prompt/dialog
// (owned by the caller, WS-5 step 2). Per design §9.3 (D5), `CategoryChip`
// carries no confidence prop or dot -- `source` is a provenance mark, not a
// confidence display.
import { AmountText } from '@/components/AmountText';
import { CategoryChip } from '@/components/CategoryChip';
import { MerchantName } from '@/components/MerchantName';
import { TypeBadge } from '@/components/TypeBadge';
import { Checkbox } from '@moneyskyline/ui';
import type { InboxUncategorizedItem } from '@/api/hooks/useInboxItems';
import type { ShellViewportBand } from '@/app/navigation/viewportBand';

export function InboxUncategorizedRow({
  item,
  band,
  accepted,
  selectable,
  selected,
  onToggleSelect,
  onAcceptSuggestion,
  onOther,
  onBusinessChoice,
}: {
  item: InboxUncategorizedItem;
  band: ShellViewportBand;
  accepted: boolean;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onAcceptSuggestion: () => void;
  onOther: () => void;
  onBusinessChoice?: (choice: 'personal' | 'business') => void;
}) {
  const isDesktop = band === 'desktop';

  const suggestionArea = (
    <div className="flex flex-wrap items-center gap-1.5">
      {item.suggestion && (
        <button
          type="button"
          onClick={onAcceptSuggestion}
          aria-pressed={accepted}
          data-testid="suggestion-chip"
          className="inline-flex"
        >
          <CategoryChip
            path={item.suggestion.path}
            source={accepted ? 'user' : item.suggestion.source}
          />
          {accepted && <span className="ml-1 text-xs">✓</span>}
        </button>
      )}
      {!item.suggestion && (
        <span className="text-xs text-muted-foreground" data-testid="no-suggestion">
          no AI suggestion above threshold
        </span>
      )}
      {item.business_ambiguous ? (
        <>
          <button
            type="button"
            className="min-h-9 rounded-full border border-input px-2.5 text-xs"
            onClick={() => onBusinessChoice?.('personal')}
          >
            Personal
          </button>
        </>
      ) : (
        <button type="button" className="min-h-9 rounded-full border border-input px-2.5 text-xs" onClick={onOther}>
          Other…
        </button>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <div
        className={[
          'grid grid-cols-[40px_80px_1fr_80px_260px_120px_120px] items-center gap-2 border-b border-border/60 px-4 py-2 text-sm',
          selected || accepted ? 'bg-link-surface' : '',
        ].join(' ')}
        data-testid="inbox-row-desktop"
        data-transaction-id={item.transaction_id}
      >
        <Checkbox
          className="ms-tap-44"
          checked={selected}
          onCheckedChange={onToggleSelect}
          aria-label={`Select ${item.merchant_display}`}
        />
        <span className="text-xs text-muted-foreground">{item.posted_at}</span>
        <span className="flex items-center gap-2">
          <MerchantName display={item.merchant_display} raw={item.merchant_raw} substituted={item.merchant_substituted} />
          {item.pending && <TypeBadge kind="pending" />}
        </span>
        <span className="text-xs text-muted-foreground">{item.account_last4 ? `•${item.account_last4}` : '—'}</span>
        {suggestionArea}
        <AmountText value={item.amount} currency={item.currency} className="text-right" />
        <span className="text-right text-xs">
          {item.suggestion && (
            <button type="button" className="text-link underline" onClick={onAcceptSuggestion}>
              Accept
            </button>
          )}
          {item.suggestion && ' · '}
          <button type="button" className="text-link underline" onClick={onOther}>
            {item.business_ambiguous ? 'Personal' : 'Other'}
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-border bg-card p-3" data-testid="inbox-row" data-transaction-id={item.transaction_id}>
      <div className="flex items-start gap-2">
        {selectable && (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${item.merchant_display}`}
            className="ms-tap-44 mt-1"
          />
        )}
        <div className="flex-1">
          <div className="flex justify-between text-sm">
            <span>
              <MerchantName display={item.merchant_display} raw={item.merchant_raw} substituted={item.merchant_substituted} />{' '}
              {item.account_last4 && <span className="text-xs text-muted-foreground">•{item.account_last4}</span>}
            </span>
            <AmountText value={item.amount} currency={item.currency} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {item.posted_at}
            {item.note ? ` · ${item.note}` : ''}
          </div>
          <div className="mt-2.5">{suggestionArea}</div>
        </div>
      </div>
    </div>
  );
}

export default InboxUncategorizedRow;
